/**
 * KR-only manager for Table Top & Marketing Site locations. Each location
 * has a fixed sponsor (KR direct, or a carrier) and a list of concrete
 * bookable availability windows that agents choose from when requesting.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Pencil, Loader2, MapPin, CalendarPlus, ArrowLeft, Wand2, CalendarDays,
  ImagePlus, Image as ImageIcon,
} from 'lucide-react';
import { retailProgramAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { TimeSelect, to12h } from '@/components/common/TimeSelect';
import ImageCropperSquare from '@/components/common/ImageCropperSquare';
import ImageCropper16x9 from '@/components/common/ImageCropper16x9';
import useInternalCoverBlob from '@/components/calendar/useInternalCoverBlob';
import LocationCalendarDialog from './LocationCalendarDialog';

const CLASSIFICATIONS = [
  { value: 'table_top', label: 'Table Top' },
  { value: 'marketing_site', label: 'Marketing Site' },
];
const SPONSORS = [
  { value: 'kr', label: 'KR (direct)' },
  { value: 'devoted', label: 'Devoted' },
  { value: 'solis', label: 'Solis' },
  { value: 'elevance', label: 'Elevance' },
];
const WEEKDAYS = [
  { idx: 1, label: 'Mon' }, { idx: 2, label: 'Tue' }, { idx: 3, label: 'Wed' },
  { idx: 4, label: 'Thu' }, { idx: 5, label: 'Fri' }, { idx: 6, label: 'Sat' },
  { idx: 0, label: 'Sun' },
];
const labelOf = (list, v) => list.find((x) => x.value === v)?.label || v;
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T12:00:00`);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
};

function emptyLocation() {
  return {
    name: '', classification: 'table_top', sponsor: 'kr',
    address: '', city: '', state: 'FL', zip_code: '', notes: '',
    active: true, availability: [],
  };
}

export default function RetailLocationsDialog({ open, onOpenChange, onChanged }) {
  const [mode, setMode] = useState('list'); // 'list' | 'edit'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [calLoc, setCalLoc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await retailProgramAPI.listLocations();
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) { setMode('list'); setForm(null); setEditingId(null); return; }
    load();
  }, [open, load]);

  const openNew = () => { setForm(emptyLocation()); setEditingId(null); setMode('edit'); };
  const openEdit = (loc) => {
    setForm({
      name: loc.name || '', classification: loc.classification || 'table_top',
      sponsor: loc.sponsor || 'kr', address: loc.address || '', city: loc.city || '',
      state: loc.state || 'FL', zip_code: loc.zip_code || '', notes: loc.notes || '',
      active: loc.active !== false, availability: loc.availability || [],
      photo_url: loc.photo_url || null, cover_url: loc.cover_url || null,
    });
    setEditingId(loc.location_id);
    setMode('edit');
  };

  const setField = (patch) => setForm((p) => ({ ...p, ...patch }));

  const validate = () => {
    if (!form.name.trim()) return 'Name is required.';
    if (!form.address.trim()) return 'Address is required.';
    if (!form.city.trim()) return 'City is required.';
    if (!/^\d{5}(-\d{4})?$/.test(form.zip_code.trim())) return 'ZIP code looks off (5 digits).';
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), classification: form.classification, sponsor: form.sponsor,
        address: form.address.trim(), city: form.city.trim(),
        state: (form.state || 'FL').toUpperCase().slice(0, 2), zip_code: form.zip_code.trim(),
        notes: form.notes.trim() || null, active: form.active,
        availability: form.availability.map((w) => ({
          window_id: w.window_id, date: w.date, start: w.start, end: w.end,
        })),
      };
      if (editingId) await retailProgramAPI.updateLocation(editingId, payload);
      else await retailProgramAPI.createLocation(payload);
      toast.success(editingId ? 'Location updated' : 'Location created');
      setMode('list');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"? Locations with active requests are deactivated instead.`)) return;
    try {
      const r = await retailProgramAPI.deleteLocation(loc.location_id);
      toast.success(r.data?.deactivated ? 'Location deactivated (had active requests)' : 'Location deleted');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete location');
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="retail-locations-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <MapPin size={18} className="text-emerald-600" />
            {mode === 'list' ? 'Manage Locations' : editingId ? 'Edit Location' : 'New Location'}
          </DialogTitle>
        </DialogHeader>

        {mode === 'list' ? (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-xs text-slate-500">
                Table Top &amp; Marketing Site venues agents can request.
              </p>
              <Button size="sm" onClick={openNew} data-testid="location-new-btn">
                <Plus size={14} className="mr-1" /> New Location
              </Button>
            </div>
            {loading ? (
              <div className="p-8 text-center text-slate-500">
                <Loader2 className="animate-spin inline mr-2" size={16} />Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-slate-500" data-testid="locations-empty">
                No locations yet. Click <span className="font-medium">New Location</span> to add one.
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((loc) => (
                  <div key={loc.location_id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3"
                    data-testid={`location-row-${loc.location_id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 truncate">{loc.name}</span>
                        <Badge variant="outline" className="text-[10px] bg-slate-50">{labelOf(CLASSIFICATIONS, loc.classification)}</Badge>
                        <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">{labelOf(SPONSORS, loc.sponsor)}</Badge>
                        {!loc.active && <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">Inactive</Badge>}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {loc.address}, {loc.city}, {loc.state} {loc.zip_code}
                      </div>
                      <div className="text-[11px] text-emerald-600 mt-0.5">
                        {loc.available_count} available · {loc.window_count} total windows
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => setCalLoc(loc)} data-testid={`location-calendar-${loc.location_id}`} title="View availability calendar">
                        <CalendarDays size={14} className="text-emerald-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(loc)} data-testid={`location-edit-${loc.location_id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(loc)} data-testid={`location-delete-${loc.location_id}`}>
                        <Trash2 size={14} className="text-rose-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <LocationEditor
            form={form}
            setField={setField}
            saving={saving}
            locationId={editingId}
            onBack={() => setMode('list')}
            onSave={save}
            onImagesChanged={load}
          />
        )}
      </DialogContent>
    </Dialog>
    <LocationCalendarDialog
      open={!!calLoc}
      onOpenChange={(v) => { if (!v) setCalLoc(null); }}
      locationId={calLoc?.location_id}
      locationName={calLoc?.name}
    />
    </>
  );
}

function LocationEditor({ form, setField, saving, locationId, onBack, onSave, onImagesChanged }) {
  const sorted = useMemo(
    () => [...(form.availability || [])].sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start)),
    [form.availability],
  );

  // Add one or many windows in a single state update (avoids stale-closure
  // loss when bulk-generating several windows at once).
  const addWindows = (ws) => {
    const next = [...(form.availability || [])];
    ws.forEach((w) => {
      const exists = next.some(
        (x) => x.date === w.date && x.start === w.start && x.end === w.end);
      if (!exists) next.push({ ...w });
    });
    setField({ availability: next });
  };
  const addWindow = (w) => addWindows([w]);
  const removeWindow = (idx) => {
    const target = sorted[idx];
    setField({
      availability: (form.availability || []).filter(
        (x) => !(x.date === target.date && x.start === target.start && x.end === target.end)),
    });
  };

  return (
    <div className="space-y-4" data-testid="location-editor">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs">Location name *</Label>
          <Input value={form.name} onChange={(e) => setField({ name: e.target.value })}
            placeholder="e.g. Walmart Supercenter — Kissimmee" data-testid="location-name" />
        </div>
        <div>
          <Label className="text-xs">Classification *</Label>
          <Select value={form.classification} onValueChange={(v) => setField({ classification: v })}>
            <SelectTrigger data-testid="location-classification"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CLASSIFICATIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Sponsor *</Label>
          <Select value={form.sponsor} onValueChange={(v) => setField({ sponsor: v })}>
            <SelectTrigger data-testid="location-sponsor"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SPONSORS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Address *</Label>
          <AddressAutocomplete
            value={form.address}
            onChange={(v) => setField({ address: v })}
            onPlaceSelected={(parsed) => setField({
              address: parsed.street || parsed.formatted,
              city: parsed.city || form.city,
              state: parsed.state || form.state,
              zip_code: parsed.zip || form.zip_code,
            })}
            placeholder="Start typing the venue address…"
            testId="location-address"
          />
        </div>
        <div>
          <Label className="text-xs">City *</Label>
          <Input value={form.city} onChange={(e) => setField({ city: e.target.value })} data-testid="location-city" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">State</Label>
            <Input value={form.state} maxLength={2}
              onChange={(e) => setField({ state: e.target.value.toUpperCase().slice(0, 2) })}
              data-testid="location-state" />
          </div>
          <div>
            <Label className="text-xs">ZIP *</Label>
            <Input value={form.zip_code} onChange={(e) => setField({ zip_code: e.target.value })}
              placeholder="34741" data-testid="location-zip" />
          </div>
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Notes</Label>
          <Input value={form.notes} onChange={(e) => setField({ notes: e.target.value })}
            placeholder="Optional — parking, contact, equipment…" data-testid="location-notes" />
        </div>
        <div className="col-span-2 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <Label className="text-sm">Active (agents can request it)</Label>
          <Switch checked={form.active} onCheckedChange={(v) => setField({ active: v })} data-testid="location-active" />
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 p-3 space-y-3">
        <Label className="text-xs flex items-center gap-1 text-slate-600">
          <ImageIcon size={13} className="text-emerald-600" /> Photos (shown to agents when picking a location)
        </Label>
        {locationId ? (
          <div className="grid sm:grid-cols-2 gap-3">
            <LocationImageField
              locationId={locationId} kind="cover" aspect="cover"
              currentUrl={form.cover_url}
              onChanged={(url) => { setField({ cover_url: url }); onImagesChanged?.(); }}
            />
            <LocationImageField
              locationId={locationId} kind="photo" aspect="square"
              currentUrl={form.photo_url}
              onChanged={(url) => { setField({ photo_url: url }); onImagesChanged?.(); }}
            />
          </div>
        ) : (
          <p className="text-[11px] text-slate-400 italic" data-testid="location-images-hint">
            Save the location first, then re-open it to add a cover and photo.
          </p>
        )}
      </div>

      <AvailabilityEditor windows={sorted} onAdd={addWindow} onBulkAdd={addWindows} onRemove={removeWindow} />

      <div className="flex justify-between pt-2 border-t border-slate-100">
        <Button variant="outline" onClick={onBack} disabled={saving} data-testid="location-back">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <Button onClick={onSave} disabled={saving} data-testid="location-save">
          {saving && <Loader2 size={14} className="mr-1 animate-spin" />}
          Save Location
        </Button>
      </div>
    </div>
  );
}

function LocationImageField({ locationId, kind, aspect, currentUrl, onChanged }) {
  const [pendingFile, setPendingFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const blobUrl = useInternalCoverBlob(currentUrl || null);
  const inputId = `loc-img-${kind}-${locationId}`;
  const isCover = aspect === 'cover';
  const Cropper = isCover ? ImageCropper16x9 : ImageCropperSquare;

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(f.type)) {
      toast.error('Use a JPG, PNG or WebP image.');
      return;
    }
    setPendingFile(f);
  };

  const uploadCropped = async (file) => {
    setBusy(true);
    try {
      const r = await retailProgramAPI.uploadLocationImage(locationId, kind, file);
      setPendingFile(null);
      toast.success(`${isCover ? 'Cover' : 'Photo'} updated`);
      onChanged?.(r.data?.[`${kind}_url`] || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const removeImg = async () => {
    setBusy(true);
    try {
      await retailProgramAPI.deleteLocationImage(locationId, kind);
      toast.success(`${isCover ? 'Cover' : 'Photo'} removed`);
      onChanged?.(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`location-image-${kind}`}>
      <p className="text-[11px] font-medium text-slate-500 mb-1">{isCover ? 'Cover (wide)' : 'Photo (square)'}</p>
      <div className={`relative rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center ${isCover ? 'aspect-video' : 'aspect-square'}`}>
        {blobUrl ? (
          <img src={blobUrl} alt={kind} className="w-full h-full object-cover" data-testid={`location-image-${kind}-preview`} />
        ) : (
          <span className="text-slate-400 text-xs flex flex-col items-center gap-1">
            <ImagePlus size={20} /> No {isCover ? 'cover' : 'photo'}
          </span>
        )}
        {busy && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 className="animate-spin text-white" size={18} /></div>}
      </div>
      <div className="flex items-center gap-1.5 mt-1.5">
        <label htmlFor={inputId} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 cursor-pointer">
          <ImagePlus size={12} /> {blobUrl ? 'Change' : 'Upload'}
        </label>
        <input id={inputId} type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} className="hidden" data-testid={`location-image-${kind}-input`} />
        {blobUrl && (
          <button type="button" onClick={removeImg} disabled={busy}
            className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 disabled:opacity-50"
            data-testid={`location-image-${kind}-remove`}>
            <Trash2 size={12} /> Remove
          </button>
        )}
      </div>
      <Cropper
        file={pendingFile}
        onCropped={uploadCropped}
        onCancel={() => setPendingFile(null)}
        title={isCover ? 'Adjust location cover' : 'Adjust location photo'}
      />
    </div>
  );
}

function AvailabilityEditor({ windows, onAdd, onBulkAdd, onRemove }) {
  const [single, setSingle] = useState({ date: '', start: '10:00', end: '14:00' });
  const [bulk, setBulk] = useState({
    from: '', to: '', start: '10:00', end: '14:00', days: new Set([1, 2, 3, 4, 5]),
  });

  const addSingle = () => {
    if (!single.date) { toast.error('Pick a date for the window.'); return; }
    if (single.end <= single.start) { toast.error('End time must be after start.'); return; }
    onAdd({ date: single.date, start: single.start, end: single.end });
  };

  const toggleDay = (idx) => setBulk((b) => {
    const days = new Set(b.days);
    if (days.has(idx)) days.delete(idx); else days.add(idx);
    return { ...b, days };
  });

  const generate = () => {
    if (!bulk.from || !bulk.to) { toast.error('Pick a date range.'); return; }
    if (bulk.end <= bulk.start) { toast.error('End time must be after start.'); return; }
    if (bulk.days.size === 0) { toast.error('Pick at least one weekday.'); return; }
    const out = [];
    const d = new Date(`${bulk.from}T12:00:00`);
    const end = new Date(`${bulk.to}T12:00:00`);
    let guard = 0;
    while (d <= end && guard < 400) {
      if (bulk.days.has(d.getDay())) {
        out.push({ date: d.toISOString().slice(0, 10), start: bulk.start, end: bulk.end });
      }
      d.setDate(d.getDate() + 1);
      guard += 1;
    }
    if (out.length === 0) { toast.error('No matching dates in that range.'); return; }
    onBulkAdd(out);
    toast.success(`Added ${out.length} window(s)`);
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="availability-editor">
      <Label className="text-xs flex items-center gap-1.5">
        <CalendarPlus size={13} className="text-purple-500" /> Availability windows
      </Label>

      {/* Single window */}
      <div className="grid grid-cols-[1.2fr_1fr_1fr_auto] gap-2 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Date</Label>
          <Input type="date" value={single.date}
            onChange={(e) => setSingle({ ...single, date: e.target.value })} data-testid="avail-single-date" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Start</Label>
          <TimeSelect value={single.start} onChange={(v) => setSingle({ ...single, start: v })} testId="avail-single-start" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">End</Label>
          <TimeSelect value={single.end} onChange={(v) => setSingle({ ...single, end: v })} testId="avail-single-end" />
        </div>
        <Button variant="outline" size="sm" onClick={addSingle} data-testid="avail-single-add">
          <Plus size={13} />
        </Button>
      </div>

      {/* Bulk generator */}
      <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 space-y-2">
        <p className="text-[11px] font-medium text-slate-500 flex items-center gap-1">
          <Wand2 size={12} className="text-purple-500" /> Bulk add by weekday
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">From</Label>
            <Input type="date" value={bulk.from} onChange={(e) => setBulk({ ...bulk, from: e.target.value })} data-testid="avail-bulk-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">To</Label>
            <Input type="date" value={bulk.to} onChange={(e) => setBulk({ ...bulk, to: e.target.value })} data-testid="avail-bulk-to" />
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {WEEKDAYS.map((w) => (
            <button key={w.idx} type="button" onClick={() => toggleDay(w.idx)}
              data-testid={`avail-bulk-day-${w.idx}`}
              className={`px-2 py-1 rounded-md text-[11px] font-medium border transition-colors ${bulk.days.has(w.idx) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200'}`}>
              {w.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Start</Label>
            <TimeSelect value={bulk.start} onChange={(v) => setBulk({ ...bulk, start: v })} testId="avail-bulk-start" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">End</Label>
            <TimeSelect value={bulk.end} onChange={(v) => setBulk({ ...bulk, end: v })} testId="avail-bulk-end" />
          </div>
          <Button variant="outline" size="sm" onClick={generate} data-testid="avail-bulk-generate">
            <Wand2 size={13} className="mr-1" /> Generate
          </Button>
        </div>
      </div>

      {/* Window list */}
      {windows.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic">No windows yet — agents won't be able to request this location.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid="avail-window-list">
          {windows.map((w, i) => (
            <span key={`${w.date}-${w.start}-${w.end}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 pl-2.5 pr-1 py-1 text-[11px]">
              <span className="text-slate-700">{fmtDate(w.date)} · {to12h(w.start)}–{to12h(w.end)}</span>
              <button type="button" onClick={() => onRemove(i)} className="text-slate-400 hover:text-rose-500"
                data-testid={`avail-window-remove-${i}`}>
                <Trash2 size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
